import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, MessageSquarePlus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { StatusBadge, TelegramButton, usePoll } from "@/components/common";

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [tg, setTg] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ subject: "", category: "General", message: "" });
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/customer/tickets").then((r) => {
    setTickets(r.data);
    if (active) { const u = r.data.find((t) => t.ticket_id === active.ticket_id); if (u) setActive(u); }
  }).catch(() => {});
  usePoll(() => { load(); api.get("/settings/public").then((r) => setTg(r.data.telegram_url)).catch(() => {}); }, 6000, []);

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/customer/tickets", form); toast.success("Ticket created"); setShowNew(false); setForm({ subject: "", category: "General", message: "" }); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try { await api.post(`/customer/tickets/${active.ticket_id}/reply`, { message: reply }); setReply(""); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Support</h1>
        <div className="flex gap-2">
          <TelegramButton url={tg} />
          <button onClick={() => setShowNew(true)} data-testid="new-ticket-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition">
            <MessageSquarePlus className="w-4 h-4" /> New ticket
          </button>
        </div>
      </div>

      {showNew && (
        <form onSubmit={create} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4 max-w-xl">
          <input required placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
            data-testid="ticket-subject-input" className="w-full px-4 py-2.5 rounded-xl border border-slate-300 outline-none" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-slate-300 bg-white outline-none">
            {["General", "Deposit", "Sell Order", "Payment", "Account"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <textarea required rows={4} placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
            data-testid="ticket-message-input" className="w-full px-4 py-2.5 rounded-xl border border-slate-300 outline-none" />
          <div className="flex gap-2">
            <button disabled={busy} data-testid="ticket-submit-button" className="px-4 py-2.5 rounded-xl bg-blue-600 text-white font-semibold disabled:opacity-60">{busy ? "…" : "Create ticket"}</button>
            <button type="button" onClick={() => setShowNew(false)} className="px-4 py-2.5 rounded-xl border border-slate-300 text-slate-600">Cancel</button>
          </div>
        </form>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="space-y-2" data-testid="tickets-list">
          {tickets.length === 0 ? <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-400">No tickets yet.</div> :
            tickets.map((t) => (
              <button key={t.ticket_id} onClick={() => setActive(t)} className={`w-full text-left bg-white border rounded-2xl p-4 transition ${active?.ticket_id === t.ticket_id ? "border-blue-500 shadow-sm" : "border-slate-200 hover:border-slate-300"}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-slate-400">{t.ticket_id}</span>
                  <StatusBadge status={t.status} />
                </div>
                <div className="font-semibold text-slate-900 mt-1">{t.subject}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t.category}{t.order_id ? ` · ${t.order_id}` : ""}</div>
              </button>
            ))}
        </div>

        {active && (
          <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col" data-testid="ticket-thread">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <div><div className="font-semibold text-slate-900">{active.subject}</div><div className="text-xs text-slate-400 font-mono">{active.ticket_id}</div></div>
              <StatusBadge status={active.status} />
            </div>
            {active.auto_context && active.auto_context.utr && (
              <div className="text-xs bg-slate-50 rounded-lg p-3 mt-3 text-slate-600 space-y-0.5">
                <div>Expected: ₹{active.auto_context.expected_inr} · Paid: ₹{active.auto_context.paid_inr} · Remaining: ₹{active.auto_context.remaining_inr}</div>
                <div>UTR: {active.auto_context.utr}</div>
              </div>
            )}
            <div className="flex-1 space-y-3 mt-3 max-h-80 overflow-y-auto scrollbar-thin">
              {active.messages.map((m, i) => (
                <div key={i} className={`max-w-[85%] rounded-xl p-3 text-sm ${m.sender === "customer" ? "bg-blue-50 ml-auto" : "bg-slate-100"}`}>
                  <div className="text-xs font-semibold text-slate-500 mb-0.5">{m.name}</div>
                  {m.message}
                  <div className="text-[10px] text-slate-400 mt-1">{new Date(m.at).toLocaleString()}</div>
                </div>
              ))}
            </div>
            {!["Closed", "Resolved"].includes(active.status) && (
              <div className="flex gap-2 mt-3 pt-3 border-t border-slate-200">
                <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type a reply…" data-testid="ticket-reply-input"
                  className="flex-1 px-3 py-2 rounded-xl border border-slate-300 outline-none text-sm" />
                <button onClick={sendReply} disabled={busy} data-testid="ticket-reply-send" className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold disabled:opacity-60">Send</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
